import React from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Chip, MenuItem,
  TextField, Tooltip, Typography
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import PlaybookStringListField from './PlaybookStringListField';
import {
  BEHAVIOR_DESCRIPTIONS,
  BEHAVIOR_LABELS,
  PlaybookItem
} from '../../types/discoveryPlaybook';

interface PlaybookItemEditorProps {
  item: PlaybookItem;
  onChange: (item: PlaybookItem) => void;
}

/** Chip colours per behaviour. `avoid` reads as a warning; the rest are neutral. */
const BEHAVIOR_CHIP_COLOR: Record<string, 'default' | 'primary' | 'secondary' | 'warning'> = {
  do: 'primary',
  respond: 'secondary',
  avoid: 'warning',
  metric: 'default'
};

/**
 * Edit one checklist item's coaching content.
 *
 * WHAT IS DELIBERATELY READ-ONLY
 * ------------------------------
 * `key`, `rubricAspect` and `behavior` are shown but not editable, and the API
 * ignores them if posted anyway. `rubricAspect` joins this item to the
 * post-call rubric — rename it and the item still coaches but can never be
 * scored, silently. `behavior` decides which speaker's turn can satisfy the
 * item, so changing it would quietly break detection routing rather than
 * produce a visible error.
 */
const PlaybookItemEditor: React.FC<PlaybookItemEditorProps> = ({ item, onChange }) => {
  const update = (patch: Partial<PlaybookItem>) => onChange({ ...item, ...patch });

  return (
    <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" width="100%">
          <Tooltip title={BEHAVIOR_DESCRIPTIONS?.[item?.behavior] ?? ''}>
            <Chip
              label={BEHAVIOR_LABELS?.[item?.behavior] ?? item?.behavior}
              size="small"
              color={BEHAVIOR_CHIP_COLOR?.[item?.behavior] ?? 'default'}
              variant="outlined"
            />
          </Tooltip>
          <Typography variant="body2" fontWeight={600}>
            {item?.label || item?.key}
          </Typography>
          <Box flexGrow={1} />
          <Tooltip title="Joins this item to the post-call rubric. Read-only.">
            <Typography variant="caption" color="text.secondary">
              {item?.rubricAspect}
            </Typography>
          </Tooltip>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <TextField
          fullWidth
          size="small"
          label="Label"
          helperText="Shown on the rep's live checklist."
          value={item?.label ?? ''}
          onChange={(event) => update({ label: event.target.value })}
        />

        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          label="Intent"
          helperText="What counts as satisfying this, in the detector's words. This is the bar — exemplars are only one way to reach it."
          value={item?.intent ?? ''}
          onChange={(event) => update({ intent: event.target.value })}
          sx={{ mt: 2 }}
        />

        <PlaybookStringListField
          label="Exemplars"
          helperText="Verbatim lines the rep can read aloud. Shown as the card's hero text."
          values={item?.exemplars ?? []}
          onChange={(exemplars) => update({ exemplars })}
          placeholder="Here's what I want to make sure we cover today…"
        />

        {item?.behavior === 'respond' && (
          <PlaybookStringListField
            label="Follow-ups"
            helperText="Offered to the rep the moment the prospect opens this door."
            values={item?.followUps ?? []}
            onChange={(followUps) => update({ followUps })}
          />
        )}

        {(item?.subConditions?.length ?? 0) > 0 && (
          <PlaybookStringListField
            label="Both halves required"
            helperText="A compound item. Only one half present shows as partial rather than hit."
            values={item?.subConditions ?? []}
            onChange={(subConditions) => update({ subConditions })}
          />
        )}

        {item?.trigger && (
          <Box mt={2} p={2} bgcolor="action.hover" borderRadius={1}>
            <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
              Opens when the prospect…
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              value={item?.trigger?.cue ?? ''}
              onChange={(event) =>
                update({ trigger: { ...item.trigger!, cue: event.target.value } })
              }
            />
            <Box display="flex" gap={2} mt={1.5}>
              <TextField
                size="small"
                type="number"
                label="Window (seconds)"
                helperText="Before it demotes to an open loop — never a failure."
                value={item?.trigger?.windowSeconds ?? 90}
                onChange={(event) =>
                  update({
                    trigger: { ...item.trigger!, windowSeconds: Number(event.target.value) }
                  })
                }
              />
              <TextField
                size="small"
                type="number"
                label="Window (turns)"
                value={item?.trigger?.windowTurns ?? 6}
                onChange={(event) =>
                  update({
                    trigger: { ...item.trigger!, windowTurns: Number(event.target.value) }
                  })
                }
              />
            </Box>
          </Box>
        )}

        {item?.violation && (
          <Box mt={2} p={2} bgcolor="action.hover" borderRadius={1}>
            <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
              Guardrail
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label="Fires when"
              value={item?.violation?.cue ?? ''}
              onChange={(event) =>
                update({ violation: { ...item.violation!, cue: event.target.value } })
              }
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label="Why it costs"
              helperText="Mechanism, not judgement — what it costs them, never 'you shouldn't have'."
              value={item?.violation?.why ?? ''}
              onChange={(event) =>
                update({ violation: { ...item.violation!, why: event.target.value } })
              }
              sx={{ mt: 1.5 }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label="Recovery line"
              helperText="What the rep says RIGHT NOW to get back on track. A guardrail without one is just a scold."
              value={item?.violation?.recovery ?? ''}
              onChange={(event) =>
                update({ violation: { ...item.violation!, recovery: event.target.value } })
              }
              sx={{ mt: 1.5 }}
            />
            <TextField
              select
              size="small"
              label="Severity"
              helperText="Critical bypasses the cooldown and waits for acknowledgement."
              value={item?.violation?.severity ?? 'warn'}
              onChange={(event) =>
                update({
                  violation: { ...item.violation!, severity: event.target.value as 'warn' | 'critical' }
                })
              }
              sx={{ mt: 1.5, minWidth: 220 }}
            >
              <MenuItem value="warn">Warn</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </TextField>

            {(item?.violation?.suppressWhen?.length ?? 0) > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                Goes silent once these are hit: {item?.violation?.suppressWhen?.join(', ')}.
                The same sentence is a violation early and correct later, so this is fixed
                by the rubric rather than configurable.
              </Typography>
            )}
          </Box>
        )}

        {item?.metric && (
          <Typography variant="caption" color="text.secondary" display="block" mt={2}>
            Computed from speaker statistics: satisfied when the prospect holds at least{' '}
            {Math.round((item?.metric?.threshold ?? 0.7) * 100)}% of the talking by minute{' '}
            {item?.metric?.byMinute ?? 10}. Never sent to a model, so it is not editable here.
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default PlaybookItemEditor;
